import { request } from './api';
import type { VisualProposal, VisualProposalChoice } from '../../../server/intelligence/visualDirection/index.js';

export type SeriesStartMode = 'idea' | 'story' | 'episodes';
export type MemoSource = 'you' | 'astra' | 'open';

export interface SeriesRow {
  id: string;
  orgId: string;
  projectId: string;
  title: string;
  format: string;
  startMode: SeriesStartMode;
  targetEpisodeCount: number | null;
  defaultEpisodeSeconds: number;
  brief: {
    answers?: Record<string, string>;
    agreement?: { at: string; by: { id: string; name: string }; lines: { id: string; value: string; source: MemoSource }[] } | null;
    originalInput?: { idea?: string; startMode?: string; at?: string };
    sourceMaterials?: { id: string; kind: string; name: string; text: string }[];
    convertedFrom?: { projectId: string; name: string; at: string; episodeId?: string; linkedAt?: string };
    answerProposals?: { field: string; previous: string; proposal: string }[];
  };
  activeMasterRevisionId: string | null;
  activePlanRevisionId: string | null;
  version: number;
  updatedAt: string;
  archivedAt?: string | null;
}

export interface MasterBeat { id: string; text: string; kind: string; reveal: string }
export interface MasterStory {
  premise: string;
  logline: string;
  arc: string;
  turns: string[];
  ending: string;
  characters: { id: string; name: string; want: string; journey: string; secret: string }[];
  beats: MasterBeat[];
  content: string;
}

export interface PlanEpisode {
  id: string;
  title: string;
  targetSeconds: number;
  hook: string;
  objective: string;
  beats: string[];
  turn: string;
  ending: string;
  endingKind: 'hook' | 'resolution';
  characters: string[];
  locations: string[];
  startState: string[];
  endState: string[];
  masterBeatIds: string[];
  dependsOn: string[];
  reveals: { fact: string; knownBy: string[]; toAudience: boolean }[];
  withholds: string[];
  splitFrom?: string;
  mergedFrom?: string[];
  sourceScript?: { materialId: string; index: number; number: number };
}
export interface EpisodePlan { episodes: PlanEpisode[]; notes: string }
export interface SeriesLookContent { control: VisualProposal['control']; summary: string; why: string; choices: VisualProposalChoice[]; changes: VisualProposal['changes']; rejected: VisualProposal['rejected']; libraryVersion: string }

export interface ImportFinding { severity: 'warning' | 'info'; message: string; number: number | null }
export interface ImportReport {
  episodes: { number: number; title: string; scenes: number; words: number; estimatedSeconds: number }[];
  characters: string[];
  findings: ImportFinding[];
}

export interface Revision<T> {
  id: string;
  kind: 'master' | 'plan' | 'visual';
  number: number;
  status: 'draft' | 'approved' | 'superseded' | 'declined';
  content: T;
  source: { kind: string; jobId?: string; note?: string; basedOn?: string | null; report?: ImportReport };
  pinnedMasterRevisionId: string | null;
  createdBy: string;
  createdAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
}
export type RevisionMeta = Omit<Revision<unknown>, 'content'>;

export interface PlanFinding { severity: 'blocker' | 'warning'; code: string; message: string; episodeId: string | null }
export interface PlanValidation { ok: boolean; findings: PlanFinding[]; totals: { episodeCount: number; plannedTotalSeconds: number } }

export interface SeriesEpisodeRow {
  id: string;
  projectId: string | null;
  position: number;
  title: string;
  targetSeconds: number;
  outline: PlanEpisode;
  planRevisionId: string;
  masterRevisionId: string | null;
  writingStatus: 'not-started' | 'drafting' | 'approved';
  productionStatus: string;
  deliveryStatus: string;
  status: { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' };
  openImpacts: number;
  spend: { estimated: number; actual: number };
}

export interface SeriesImpact {
  id: string;
  episodeId: string | null;
  targetKind: 'plan' | 'episode' | 'asset';
  targetId: string | null;
  sourceKind: string;
  sourceRevisionId: string;
  previousRevisionId: string | null;
  reason: string;
  status: string;
  createdAt: string;
}

export interface EpisodeState { wardrobe?: string; injuries?: string; possessions?: string; condition?: string; notes?: string }
export interface SeriesVoice { provider: 'elevenlabs' | 'local' | 'other'; voiceId: string; name: string; notes: string }
export interface SeriesAsset {
  id: string;
  kind: 'character' | 'location' | 'prop' | 'voice';
  name: string;
  tag: string;
  activeVersionId: string | null;
  activeVersion: { id: string; number: number; fingerprint: string; source: { projectId?: string; episodeId?: string; cardApprovedBy?: { id?: string; name?: string } | null }; voice: SeriesVoice | null; approvedBy: string | null; approvedAt: string | null } | null;
  versionCount: number;
  bindings: { episodeId: string; versionId: string; versionNumber: number | null; current: boolean; state: EpisodeState }[];
}

export interface SeriesQuestion { id: string; question: string; why: string; choices: string[] }

export interface SeriesDeliveryRow {
  episodeId: string;
  number: number;
  title: string;
  projectId: string | null;
  targetSeconds: number;
  deliveryStatus: 'not-started' | 'exported' | 'delivered';
  master: { mediaId: string; name: string; size: number; durationSeconds: number | null; createdAt: string; url: string } | null;
}
export interface SeriesDelivery { episodes: SeriesDeliveryRow[]; exported: number; delivered: number; total: number; complete: boolean; runtimeSeconds: number }

export interface SeriesView {
  series: SeriesRow;
  role: string;
  dealMemo: {
    ready: boolean;
    agreed: boolean;
    agreement: SeriesRow['brief']['agreement'];
    lines: { id: string; label: string; value: string; source: MemoSource; note?: string }[];
    runtime: { episodeCount: number | null; episodeSeconds: number | null; plannedTotalSeconds: number | null };
  };
  questions: SeriesQuestion[];
  master: { active: Revision<MasterStory> | null; draft: Revision<MasterStory> | null; history: RevisionMeta[] };
  plan: { active: Revision<EpisodePlan> | null; draft: Revision<EpisodePlan> | null; validation: PlanValidation | null; stale: boolean; history: RevisionMeta[] };
  episodes: SeriesEpisodeRow[];
  removedEpisodes: { id: string; title: string; projectId: string }[];
  impacts: SeriesImpact[];
  assets: SeriesAsset[];
  asset?: { assetId: string; versionId: string; number: number; unchanged?: boolean; impacts?: number };
  visual: { active: Revision<SeriesLookContent> | null; draft: Revision<SeriesLookContent> | null; history: Array<RevisionMeta & { summary: string }> };
  runtime: { episodeCount: number; plannedTotalSeconds: number; defaultEpisodeSeconds: number };
  archived: boolean;
  delivery: SeriesDelivery;
  budget: { capInr: number; fxINRPerUSD: number; committedInr: number; remainingInr: number | null; series: { estimated: number; actual: number }; episodes: { estimated: number; actual: number } };
  impactsCreated?: number;
  workspace?: { projectId: string; created: boolean };
  validation?: PlanValidation;
}

export interface SeriesLink {
  role: 'series' | 'episode';
  seriesId: string;
  seriesTitle: string;
  archived?: boolean;
  canOpenSeries: boolean;
  episode: { episodeId: string; number: number; of: number; title: string; projectId: string | null; needsReview: boolean; writingStatus: string } | null;
  previous: { episodeId: string; number: number; title: string; projectId: string | null } | null;
  next: { episodeId: string; number: number; title: string; projectId: string | null } | null;
}

export interface NewSeriesInput {
  title: string;
  format: string;
  startMode: SeriesStartMode;
  idea: string;
  answers: Record<string, string>;
  source?: string;
  sourceName?: string;
  lookPreset?: string;
  budgetInr?: number;
}

const path = (seriesId: string, rest = '') => `/api/series/${encodeURIComponent(seriesId)}${rest}`;
const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });

export const seriesApi = {
  list: (archived = false) => request<{ series: (SeriesRow & { episodes: { id: string; projectId: string | null; writingStatus: string }[] })[] }>(`/api/series${archived ? '?archived=1' : ''}`),
  convert: (projectId: string, title?: string) => request<SeriesView>(`/api/projects/${encodeURIComponent(projectId)}/convert-to-series`, post(title ? { title } : {})),
  setBudget: (view: SeriesView, limitINR: number) => request<SeriesView>(path(view.series.id, '/budget'), post({ version: view.series.version, limitINR })),
  setDelivery: (view: SeriesView, episodeId: string, status: 'delivered' | 'exported') => request<SeriesView>(path(view.series.id, `/episodes/${encodeURIComponent(episodeId)}/delivery`), post({ version: view.series.version, status })),
  archive: (view: SeriesView) => request<SeriesView>(path(view.series.id, '/archive'), post({ version: view.series.version })),
  restore: (seriesId: string) => request<SeriesView>(path(seriesId, '/restore'), post({})),
  /* The download a series leaves with: its production, every episode workspace, revisions and media. */
  exportArchive: (seriesId: string) => {
    const link = document.createElement('a');
    link.href = path(seriesId, '/export/archive');
    link.download = '';
    document.body.append(link);
    link.click();
    link.remove();
  },
  remove: (seriesId: string, confirm: string) => request<{ ok: true; deleted: string; projects: string[] }>(path(seriesId), { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  setVoice: (view: SeriesView, assetId: string, voice: Partial<SeriesVoice> | null, episodeId?: string) => request<SeriesView>(path(view.series.id, `/assets/${encodeURIComponent(assetId)}/voice`), post({ version: view.series.version, voice, ...(episodeId ? { episodeId } : {}) })),
  create: (input: NewSeriesInput) => request<SeriesView>('/api/series', post(input)),
  get: (seriesId: string) => request<SeriesView>(path(seriesId)),
  saveBrief: (view: SeriesView, answers: Record<string, string>) => request<SeriesView>(path(view.series.id, '/brief'), post({ version: view.series.version, answers })),
  applyBriefProposal: (view: SeriesView, jobId: string) => request<SeriesView>(path(view.series.id, '/brief/proposal'), post({ version: view.series.version, jobId })),
  agree: (view: SeriesView) => request<SeriesView>(path(view.series.id, '/agree'), post({ version: view.series.version })),
  draftMaster: (view: SeriesView, body: { content?: Partial<MasterStory>; jobId?: string; fromSource?: boolean }) => request<SeriesView>(path(view.series.id, '/master'), post({ version: view.series.version, ...body })),
  approveMaster: (view: SeriesView, revisionId: string) => request<SeriesView>(path(view.series.id, `/master/${encodeURIComponent(revisionId)}/approve`), post({ version: view.series.version })),
  draftPlan: (view: SeriesView, body: { content?: EpisodePlan; jobId?: string; baseRevisionId?: string; fromSource?: boolean }) => request<SeriesView>(path(view.series.id, '/plan'), post({ version: view.series.version, ...body })),
  approvePlan: (view: SeriesView, revisionId: string) => request<SeriesView>(path(view.series.id, `/plan/${encodeURIComponent(revisionId)}/approve`), post({ version: view.series.version })),
  openEpisode: (view: SeriesView, episodeId: string) => request<SeriesView>(path(view.series.id, `/episodes/${encodeURIComponent(episodeId)}/workspace`), post({})),
  visualPropose: (view: SeriesView, body: { control: string; answers: Record<string, string>; text: string }) => request<SeriesView>(path(view.series.id, '/visual/proposals'), post({ version: view.series.version, ...body })),
  visualAdjust: (view: SeriesView, revisionId: string, change: Record<string, unknown>) => request<SeriesView>(path(view.series.id, `/visual/${encodeURIComponent(revisionId)}/adjust`), post({ version: view.series.version, ...change })),
  visualApprove: (view: SeriesView, revisionId: string) => request<SeriesView>(path(view.series.id, `/visual/${encodeURIComponent(revisionId)}/approve`), post({ version: view.series.version })),
  visualDecline: (view: SeriesView, revisionId: string, defer = false) => request<SeriesView>(path(view.series.id, `/visual/${encodeURIComponent(revisionId)}/decline`), post({ version: view.series.version, defer })),
  shareAsset: (view: SeriesView, projectId: string, assetId: string, newVersion = false) => request<SeriesView>(path(view.series.id, '/assets'), post({ version: view.series.version, projectId, assetId, newVersion })),
  bindAsset: (seriesId: string, episodeId: string, assetId: string, state?: EpisodeState) => request<SeriesView>(path(seriesId, `/episodes/${encodeURIComponent(episodeId)}/assets/${encodeURIComponent(assetId)}/bind`), post(state ? { state } : {})),
  unbindAsset: (seriesId: string, episodeId: string, assetId: string) => request<SeriesView>(path(seriesId, `/episodes/${encodeURIComponent(episodeId)}/assets/${encodeURIComponent(assetId)}/unbind`), post({})),
  resolveImpact: (view: SeriesView, impactId: string, decision: 'accept' | 'keep-pinned' | 'rework', note = '') => request<SeriesView>(path(view.series.id, `/impacts/${encodeURIComponent(impactId)}/resolve`), post({ version: view.series.version, decision, note })),
};

export function formatSeconds(total: number) {
  const seconds = Math.max(0, Math.round(total || 0));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60), rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

export const seriesPath = (seriesId: string) => `/series/${encodeURIComponent(seriesId)}`;

const LOOK_STATUS: Record<string, VisualProposal['status']> = { draft: 'proposed', approved: 'accepted', superseded: 'superseded', declined: 'declined' };

/** Series look revisions shown with the same card as a film's proposals. */
export function seriesLookProposals(view: SeriesView): VisualProposal[] {
  const revisions = [...view.visual.history.map(meta => (meta.id === view.visual.active?.id ? view.visual.active : meta.id === view.visual.draft?.id ? view.visual.draft : null)).filter(Boolean)] as Revision<SeriesLookContent>[];
  return revisions.map(revision => ({
    id: revision.id,
    revision: revision.number,
    status: LOOK_STATUS[revision.status] || 'superseded',
    scope: 'series',
    control: revision.content.control,
    summary: revision.content.summary,
    why: revision.content.why,
    choices: revision.content.choices,
    changes: revision.content.changes || [],
    rejected: revision.content.rejected || [],
    baseSignature: String(revision.source.basedOn || ''),
    source: { kind: revision.source.kind },
    createdAt: revision.createdAt,
  }));
}

/** The delivery list as a file a distributor can read: every episode in order, with its master. */
export function deliveryManifest(view: SeriesView) {
  return {
    series: view.series.title,
    format: view.series.format,
    generatedAt: new Date().toISOString(),
    complete: view.delivery.complete,
    episodes: view.delivery.episodes.map(row => ({
      number: row.number,
      title: row.title,
      status: row.deliveryStatus,
      plannedSeconds: row.targetSeconds,
      master: row.master ? { file: row.master.name, durationSeconds: row.master.durationSeconds, bytes: row.master.size, exportedAt: row.master.createdAt, url: row.master.url } : null,
    })),
  };
}
